import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../api/client';
import { toast } from 'sonner';
import { pdf } from '@react-pdf/renderer';
import { saveAs } from 'file-saver';
import ChecklistPDF from '../pdf/ChecklistPDF';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle2, Printer, Download, Copy } from 'lucide-react';

export default function RunCompletePage() {
  const { runId } = useParams();
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api.get(`/runs/${runId}`).then(r => { setRun(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [runId]);

  const handleDuplicate = async () => {
    try {
      const res = await api.post(`/runs/${runId}/duplicate`);
      toast.success('Run duplicated');
      window.location.href = `/checklists/${res.data.run.id}/question`;
    } catch (err) { toast.error(err.response?.data?.detail || 'Duplication failed'); }
  };

  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      const printRes = await api.get(`/runs/${runId}/print`);
      const ctx = printRes.data.print_context;
      let logoBase64 = null;
      if (ctx.logo_url) {
        try {
          const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}${ctx.logo_url}`);
          const blob = await resp.blob();
          logoBase64 = await new Promise(resolve => { const r = new FileReader(); r.onloadend = () => resolve(r.result); r.readAsDataURL(blob); });
        } catch {}
      }
      const doc = <ChecklistPDF printContext={ctx} logoBase64={logoBase64} />;
      const blob = await pdf(doc).toBlob();
      saveAs(blob, `${(ctx.project_fields?.project_name || 'checklist').replace(/\s+/g, '_')}.pdf`);
      toast.success('PDF downloaded');
    } catch (err) { toast.error('PDF generation failed: ' + err.message); }
    finally { setDownloading(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-full text-sm text-zinc-400">Loading...</div>;
  if (!run) return <div className="flex items-center justify-center h-full text-sm text-zinc-400">Run not found</div>;

  const sectionCount = new Set((run.output_items || []).map(i => i.section_group).filter(Boolean)).size;
  const itemCount = (run.output_items || []).length;

  return (
    <div className="max-w-lg mx-auto px-6 py-12" data-testid="run-complete-page">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-50 mb-4">
          <CheckCircle2 size={28} className="text-emerald-600" />
        </div>
        <h1 className="text-xl font-bold tracking-tight text-zinc-900 mb-1">Checklist Complete</h1>
        <p className="text-sm text-zinc-500">Your project checklist has been generated</p>
      </div>

      <Card className="rounded-sm border-zinc-200 shadow-none mb-5">
        <CardContent className="p-5">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><span className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400 block mb-0.5">Project</span><span className="font-medium text-zinc-800">{run.project_fields?.project_name || '-'}</span></div>
            <div><span className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400 block mb-0.5">Client</span><span className="font-medium text-zinc-800">{run.project_fields?.client_name || '-'}</span></div>
            <div><span className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400 block mb-0.5">Engineer</span><span className="font-medium text-zinc-800">{run.project_fields?.engineer_name || '-'}</span></div>
            <div><span className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400 block mb-0.5">Items</span><span className="font-medium text-zinc-800">{sectionCount} sections, {itemCount} items</span></div>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-2">
        <Button asChild className="w-full rounded-sm h-11 text-sm font-semibold gap-2" style={{ background: 'var(--brand-primary, #1C3557)' }}>
          <Link to={`/checklists/${runId}/print`} target="_blank" data-testid="print-btn"><Printer size={15} /> Print checklist</Link>
        </Button>
        <Button variant="outline" onClick={handleDownloadPdf} disabled={downloading} data-testid="download-pdf-btn"
          className="w-full rounded-sm h-11 text-sm font-medium gap-2">
          <Download size={15} /> {downloading ? 'Generating PDF...' : 'Download PDF'}
        </Button>
        <Button variant="outline" onClick={handleDuplicate} data-testid="duplicate-run-btn"
          className="w-full rounded-sm h-11 text-sm font-medium gap-2">
          <Copy size={15} /> Duplicate this run
        </Button>
        <Link to="/checklists" className="block text-center text-xs text-zinc-400 hover:text-zinc-600 font-medium mt-4">Back to my checklists</Link>
      </div>
    </div>
  );
}
